export enum UserRole {
  Unknown = 0,
  Admin = 1,
  AirportAdmin = 2,
  AirportOfficer = 3,
}

/** Which submissions the dashboard counts. */
export type DashboardDataset = "person-security" | "self-serve";

/** GET /v3/person-security-submissions/dashboard */
export type DashboardStats = {
  dataset: DashboardDataset;
  /** Location names the response is limited to; null when unrestricted. */
  locations: string[] | null;
  /** Location the breakdown is narrowed to; null when it covers the scope. */
  location: string | null;
  timezone: string;
  generatedAt: string;
  summary: {
    today: number;
    yesterday: number;
    thisMonth: number;
    lastMonth: number;
    todayVsYesterdayPercent: number | null;
    thisMonthVsLastMonthPercent: number | null;
  };
  range: { fromDate: string; toDate: string; days: number; total: number };
  byDay: Array<{ date: string; count: number }>;
  byLocation: Array<{ location: string; count: number }>;
  byHour: Array<{ hour: number; count: number }>;
  /** The same day in half-hour slots (48, zero-filled). */
  byHalfHour: HalfHourCount[];
};

export type HalfHourCount = {
  /** 0..47: half hours since midnight. */
  slot: number;
  hour: number;
  minute: 0 | 30;
  count: number;
};

/** GET /v3/person-security-submissions/dashboard/forecast */
export type DashboardForecast = {
  dataset: DashboardDataset;
  locations: string[] | null;
  location: string | null;
  timezone: string;
  generatedAt: string;
  forecast: {
    fromDate: string;
    toDate: string;
    days: number;
    total: number;
    /** Last day of history the forecast was built from. */
    historyThrough: string;
  };
  method: {
    /** "last-year": seasonal baseline scaled and blended; "recent-weeks": fallback. */
    basis: "last-year" | "recent-weeks";
    growthPercent: number | null;
    growthWeeks: number;
    growthQuarterPercent: number | null;
    growthHalfYearPercent: number | null;
    recentWeeks: number;
    recentBlendPercent: number;
    dataStart: string | null;
  };
  byDay: Array<{ date: string; count: number; lastYear: number | null }>;
  byLocation: Array<{ location: string; count: number }>;
  /** Expected submissions per hour on an average forecast day (whole people). */
  byHour: Array<{ hour: number; count: number }>;
  /** The same expected day in half-hour slots; they sum to the day. */
  byHalfHour: HalfHourCount[];
};

export type ForecastTotal = {
  fromDate: string;
  toDate: string;
  /** The day the forecast was made "as at"; history runs up to the day before. */
  asOf: string;
  total: number;
  basis: "last-year" | "recent-weeks";
};

/** The two headline forecasts behind the dashboard counters. */
export type DashboardForecastSummary = {
  dataset: DashboardDataset;
  locations: string[] | null;
  timezone: string;
  generatedAt: string;
  /** Today's expected total, forecast this morning. */
  today: ForecastTotal;
  /** This month's expected total, forecast as at the 1st (as the chart shows). */
  thisMonth: ForecastTotal;
};

export type User = {
  id: string;
  username: string;
  email: string;
  createdAt: Date;
  updatedAt: Date;
  lastLoginAt?: Date;
  hashedPassword?: string;
  roles: UserRole[];
  firstName: string;
  lastName: string;
};

export type CreateUserRequest = {
  username: string;
  email: string;
  password?: string;
  role?: number;
  firstName: string;
  lastName: string;
  active: boolean;
  defaultLogoutTime?: string;
  selfServeLocationId?: string;
};

export type UpdateUserRequest = {
  id: string;
  username: string;
  email: string;
  password?: string;
  role?: number;
  firstName: string;
  lastName: string;
  active: boolean;
  defaultLogoutTime?: string;
  selfServeLocationId?: string;
};

export type CreateAirportRequest = {
  name: string;
  timezone: string;
};

export type UpdateAirportRequest = {
  name?: string;
  timezone?: string;
};

export type CreateAuditPolicyRequest = {
  title: string;
  securityTierId: string;
  type: number;
  randomizedChecksGroupIds: string[];
  securityCheckIds: string[];
};

export type UpdateAuditPolicyRequest = {
  title: string;
  securityTierId: string;
  type: number;
  randomizedChecksGroupIds: string[];
  securityCheckIds: string[];
};

export type CreateLocationRequest = {
  name: string;
  active: boolean;
  display: boolean;
  fixedLocation: boolean;
  fixedLocationReportingLatitude?: number;
  fixedLocationReportingLongitude?: number;
};

export type UpdateLocationRequest = {
  name: string;
  display: boolean;
  active: boolean;
  hasVehicles: boolean;
  hasTruckSeals?: boolean;
  vehicleRequired?: boolean;
  fixedLocation: boolean;
  fixedLocationReportingLatitude?: number;
  fixedLocationReportingLongitude?: number;
};

// Unified kiosk app: access-level option offered by a kiosk location, mapping
// to a standard/exempt (Operations Staff) zone location for tier routing.
export type SaveLocationAccessLevelRequest = {
  label: string;
  sortOrder: number;
  standardLocationId: string;
  exemptLocationId: string;
  active?: boolean;
};

// Per-location operational need offered on an AEV location's PreCheck kiosks
// (the AEV-location counterpart of a Self Serve location's requirements).
export type SaveLocationOperationalNeedRequest = {
  name: string;
  sortOrder: number;
  active?: boolean;
};

export type CreateLocationAuditConfigurationRequest = {
  locationId: string;
  requiredScan: number;
  extensionThreshold: number;
  extensionAmount: number;
  locationAuditPolicies: {
    percentage: number;
    auditPolicyId: string;
  }[];
};

export type LocationAuditPolicy = {
  id: number;
  auditPolicyId: string;
  percentage: number;
};

export type CreateReportConfigurationRequest = {
  reportFrequency: string;
  sendFrequency: string;
  sendTime: number;
  recipientEmailAddresses: string[];
};

export type UpdateReportConfigurationRequest = {
  reportFrequency?: string;
  sendFrequency?: string;
  sendTime?: number;
  recipientEmailAddresses?: string[];
};

export type CreateSecurityCheckRequest = {
  name: string;
  key: string;
  defaultResult: string;
  active: boolean;
};

export type UpdateSecurityCheckRequest = {
  name?: string;
  type?: string;
  key?: string;
  defaultResult?: string;
  active?: boolean;
};

export type CreateSecurityTierRequest = {
  name: string;
  level: number;
};

export type UpdateSecurityTierRequest = {
  name?: string;
  level?: number;
};

export enum VisitorPassStatus {
  Unknown = 0,
  Requested = 1,
  Approved = 2,
  Declined = 3,
  Expired = 4,
  Cancelled = 5,
  Printed = 6,
}

export type AsicSearchParams = {
  firstName?: string;
  lastName?: string;
  companyName?: string;
  asicId?: string;
};

export type PersonSecuritySubmissionSearchParams = {
  locationName?: string;
  asicId?: string;
  firstName?: string;
  lastName?: string;
  company?: string;
};

export type SelfServeAuditSearchParams = {
  asicId?: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  accessLocation?: string;
};

export type SearchFilter = {
  id: number;
  searchField: string;
  searchQuery: string;
};

// Offline Access (outage resilience, 2026-09-01).
//
// A single shared PIN, rotated on a schedule and emailed out, which lets a
// security officer unlock manual entry on the Officer Dashboard while the
// backend is unreachable. Tablets receive only its hash, on the existing
// /devices/lookup heartbeat.
export type PinRotationCadence = "Daily" | "Weekly" | "Manual";

export type OfflineAccessStatus = {
  enabled: boolean;
  rotationCadence: PinRotationCadence;
  rotationHour: number;
  emailRecipients: string[];
  // Null until the first PIN has been generated.
  version: number | null;
  validFrom: string | null;
  validUntil: string | null;
  lastRotatedAt: string | null;
  isSet: boolean;
};

export type UpdateOfflineAccessConfigRequest = {
  enabled?: boolean;
  rotationCadence?: PinRotationCadence;
  rotationHour?: number;
  emailRecipients?: string[];
};

export type OfflinePinRevealLogEntry = {
  id: string;
  userId: string;
  pinVersion: number;
  revealedAt: string;
};
